import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface CartItem {
  menu_item_id: string
  name: string
  base_price: number
  variant_name?: string
  variant_price_modifier?: number
  addons?: string[]
  addons_price?: number
  quantity: number
  image?: string
  notes?: string
  code?: string
}

interface CartContextType {
  items: CartItem[]
  addItem: (item: CartItem) => void
  removeItem: (menuItemId: string, variantName?: string, addons?: string[]) => void
  updateQuantity: (menuItemId: string, quantity: number, variantName?: string, addons?: string[]) => void
  clearCart: () => void
  getTotalItems: () => number
  getSubtotal: () => number
}

const CART_STORAGE_KEY = 'qr_menu_cart'

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: ReactNode }) {
  // Load cart from localStorage on mount
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY)
      if (savedCart) {
        return JSON.parse(savedCart)
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error)
    }
    return []
  })

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items))
    } catch (error) {
      console.error('Error saving cart to localStorage:', error)
    }
  }, [items])

  const addItem = (item: CartItem) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(
        i => i.menu_item_id === item.menu_item_id &&
        i.variant_name === item.variant_name &&
        JSON.stringify(i.addons) === JSON.stringify(item.addons)
      )

      if (existingIndex >= 0) {
        const updated = [...prev]
        updated[existingIndex].quantity += item.quantity
        return updated
      }

      return [...prev, item]
    })
  }

  const removeItem = (menuItemId: string, variantName?: string, addons?: string[]) => {
    setItems(prev => prev.filter(
      i => !(i.menu_item_id === menuItemId &&
        i.variant_name === variantName &&
        JSON.stringify(i.addons) === JSON.stringify(addons))
    ))
  }

  const updateQuantity = (menuItemId: string, quantity: number, variantName?: string, addons?: string[]) => {
    if (quantity <= 0) {
      removeItem(menuItemId, variantName, addons)
      return
    }

    setItems(prev => prev.map(item => {
      if (item.menu_item_id === menuItemId &&
        item.variant_name === variantName &&
        JSON.stringify(item.addons) === JSON.stringify(addons)) {
        return { ...item, quantity }
      }
      return item
    }))
  }

  const clearCart = () => {
    setItems([])
    try {
      localStorage.removeItem(CART_STORAGE_KEY)
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error)
    }
  }

  /** Number of line items (distinct cart entries), not total quantity. */
  const getTotalItems = () => {
    return items.length
  }

  const getSubtotal = () => {
    return items.reduce((sum, item) => {
      const unitPrice = item.base_price + (item.variant_price_modifier || 0) + (item.addons_price || 0)
      return sum + (unitPrice * item.quantity)
    }, 0)
  }

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      getTotalItems,
      getSubtotal
    }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

